import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import MarketingLayout from '@/components/shared/MarketingLayout';
import {
  TrendingDown,
  ArrowRight,
  Zap,
  Bomb,
  Trophy,
  Swords,
  Lock,
  ArrowRightLeft,
  Eye,
  Shuffle,
  Ban,
  Crown,
  Users,
  Calendar,
  Target,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Scoring example data                                               */
/* ------------------------------------------------------------------ */
const wentzTrash = [
  { stat: '3 INTs thrown', pts: '+15.0' },
  { stat: '2 sacks taken', pts: '+6.0' },
  { stat: '1 fumble lost', pts: '+4.0' },
];
const wentzAnti = [
  { stat: '180 pass yards', pts: '-3.6' },
  { stat: '1 touchdown', pts: '-4.0' },
];

/* ------------------------------------------------------------------ */
/*  Sabotage card examples                                             */
/* ------------------------------------------------------------------ */
const exampleCards = [
  {
    name: 'Force Start',
    rarity: 'Common',
    rarityColor: 'text-muted-foreground',
    rarityBg: 'bg-muted/30',
    borderColor: 'border-border',
    icon: <Lock className="w-5 h-5" />,
    color: 'text-primary',
    accentBg: 'bg-primary/20',
    desc: 'Force your opponent to start a specific player. Bench warmers, meet the spotlight.',
  },
  {
    name: 'Bench Bomb',
    rarity: 'Rare',
    rarityColor: 'text-purple-400',
    rarityBg: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
    icon: <Bomb className="w-5 h-5" />,
    color: 'text-purple-400',
    accentBg: 'bg-purple-500/20',
    desc: "Lock one of their starters to the bench for the entire week. They can't move them. Brutal.",
  },
  {
    name: 'The Mole',
    rarity: 'Legendary',
    rarityColor: 'text-amber-400',
    rarityBg: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    icon: <Eye className="w-5 h-5" />,
    color: 'text-amber-400',
    accentBg: 'bg-amber-500/20',
    desc: "Secretly scout your opponent's entire roster for 3 days. Knowledge is power. Use it wisely.",
  },
];

const draftCards = [
  {
    name: 'Guess Who',
    icon: <Ban className="w-5 h-5" />,
    color: 'text-primary',
    accentBg: 'bg-primary/20',
    desc: 'Opponent can only see player headshots during the next round, no names, no stats.',
  },
  {
    name: 'Snake Reversal',
    icon: <Shuffle className="w-5 h-5" />,
    color: 'text-purple-400',
    accentBg: 'bg-purple-500/20',
    desc: 'Reverse the entire draft order for the next round. Hope you planned ahead.',
  },
  {
    name: 'Blocked Punt',
    icon: <Zap className="w-5 h-5" />,
    color: 'text-amber-400',
    accentBg: 'bg-amber-500/20',
    desc: "Force an opponent to auto-pick their next selection. They don't get to choose.",
  },
];

/* ------------------------------------------------------------------ */
/*  Roster positions                                                    */
/* ------------------------------------------------------------------ */
const rosterPositions = ['QB', 'RB', 'WR', 'TE', 'K', 'P', 'DST', 'COACH', 'IDP'];

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */
export default function HowToPlay() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'How to Play Sabotage Fantasy: Inverted Scoring Fantasy Sports';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Learn how Sabotage Fantasy works: inverted scoring, sabotage cards, draft strategy, and more. Fantasy sports where the worst players win.');
    }
  }, []);

  return (
    <MarketingLayout>

        {/* ============================================================ */}
        {/*  SECTION 1: Fantasy Sports, Flipped                          */}
        {/* ============================================================ */}
        <div className="text-center space-y-4">
          <h1 className="text-3xl md:text-5xl font-display font-bold">
            Fantasy Sports, <span className="text-gradient-sabotage">Flipped</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            In traditional fantasy, you draft superstars and pray they perform.
            In Sabotage Fantasy, you draft the <strong className="text-foreground">WORST</strong> players
            and profit from their failures. Strikeouts, fumbles, interceptions, and errors
            are your bread and butter.
          </p>
        </div>

        {/* Side-by-side comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-border bg-card/50">
            <CardContent className="p-5 text-center space-y-3">
              <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center mx-auto text-muted-foreground">
                <Trophy className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-sm text-muted-foreground">Traditional Fantasy</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 justify-center">
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span>Draft the best players</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span>Good stats = points</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span>Hope they perform</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-5 text-center space-y-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mx-auto text-primary">
                <TrendingDown className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-sm text-primary">Sabotage Fantasy</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 justify-center">
                  <ArrowRight className="w-3 h-3 text-primary" />
                  <span>Draft the <strong className="text-foreground">worst</strong> players</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <ArrowRight className="w-3 h-3 text-primary" />
                  <span>Bad stats = points</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <ArrowRight className="w-3 h-3 text-primary" />
                  <span>Hope they <strong className="text-foreground">fail</strong></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Separator />

        {/* ============================================================ */}
        {/*  SECTION 2: How Scoring Works                                */}
        {/* ============================================================ */}
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center">
            Trash Points vs <span className="text-damage">Anti-Points</span>
          </h2>
          <p className="text-sm text-muted-foreground text-center max-w-xl mx-auto">
            Your score is split into two buckets. One rewards failure. The other punishes success.
            You want the first one to be <strong className="text-foreground">way</strong> bigger.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Trash Points */}
            <Card className="border-accent/20 bg-accent/5">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-accent/20 text-accent border-accent/30 text-xs">TRASH POINTS</Badge>
                  <span className="text-xs text-muted-foreground">The good stuff</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Interceptions, fumbles, sacks, missed field goals, penalties.
                  The worse your players perform in real life, the more trash points you earn.
                </p>
              </CardContent>
            </Card>

            {/* Anti-Points */}
            <Card className="border-damage/20 bg-damage/5">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-damage/20 text-damage border-damage/30 text-xs">ANTI-POINTS</Badge>
                  <span className="text-xs text-muted-foreground">The penalties</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Touchdowns, big yardage, wins. When your players accidentally do something
                  good, you <strong className="text-foreground">lose</strong> points. Anti-points are deducted from your total.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Example player card */}
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">QB</div>
                <div>
                  <h4 className="font-bold text-sm">Carson Wentz</h4>
                  <p className="text-[11px] text-muted-foreground">Week 3 Performance</p>
                </div>
              </div>

              <div className="space-y-1.5">
                {wentzTrash.map(row => (
                  <div key={row.stat} className="flex justify-between items-center text-sm bg-accent/5 rounded px-3 py-1.5">
                    <span className="text-muted-foreground">{row.stat}</span>
                    <span className="font-bold text-accent">{row.pts}</span>
                  </div>
                ))}
                {wentzAnti.map(row => (
                  <div key={row.stat} className="flex justify-between items-center text-sm bg-damage/5 rounded px-3 py-1.5">
                    <span className="text-muted-foreground">{row.stat}</span>
                    <span className="font-bold text-damage">{row.pts}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center text-sm bg-primary/10 rounded px-3 py-2 mt-2 border border-primary/20">
                  <span className="font-bold text-foreground">NET SCORE</span>
                  <span className="font-extrabold text-primary text-lg">+17.4 SAB PTS</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-3 italic text-center">
                Wentz had a terrible game. That's <strong className="text-foreground">great</strong> for you.
              </p>
            </CardContent>
          </Card>
        </div>

        <Separator />

        {/* ============================================================ */}
        {/*  SECTION 3: The Draft                                        */}
        {/* ============================================================ */}
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center">
            Draft the Worst. <span className="text-primary">Seriously.</span>
          </h2>
          <p className="text-sm text-muted-foreground text-center max-w-xl mx-auto">
            ADP rankings are inverted. The #1 pick in Sabotage is the WORST player in the NFL.
            Your league-mates will fight over the biggest busts, not the biggest stars.
          </p>

          {/* Roster positions */}
          <Card className="border-border">
            <CardContent className="p-5 space-y-3">
              <h4 className="font-display font-bold text-sm text-muted-foreground uppercase tracking-wider">Roster Positions</h4>
              <div className="flex flex-wrap gap-2">
                {rosterPositions.map(pos => (
                  <Badge key={pos} variant="outline" className="text-xs font-bold border-primary/30 text-primary">
                    {pos}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Yes, you draft coaches and punters. Every position matters in Sabotage.
              </p>
            </CardContent>
          </Card>

          {/* Draft types */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  <h4 className="font-bold text-sm">Snake Draft</h4>
                </div>
                <p className="text-xs text-muted-foreground">
                  Live, real-time picks. Everyone joins, the clock starts, and you pick when it's your turn.
                  Fast, intense, and chaotic.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-accent" />
                  <h4 className="font-bold text-sm">Slow Draft</h4>
                </div>
                <p className="text-xs text-muted-foreground">
                  Async picks with hours per selection. Perfect for leagues spread across time zones.
                  Take your time, do your research.
                </p>
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground text-center italic">
            Oh, and you can sabotage your opponents DURING the draft. More on that below.
          </p>
        </div>

        <Separator />

        {/* ============================================================ */}
        {/*  SECTION 4: Sabotage Cards — The Secret Weapon               */}
        {/* ============================================================ */}
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center">
            Sabotage Cards: <span className="text-damage">The Secret Weapon</span>
          </h2>
          <p className="text-sm text-muted-foreground text-center max-w-xl mx-auto">
            This is what makes Sabotage Fantasy different from every other platform.
            Every team gets sabotage credits to spend against opponents. Time them right
            and you can flip a matchup completely.
          </p>

          {/* How it works */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-5 space-y-3">
              <h4 className="font-display font-bold text-sm">How Sabotage Cards Work</h4>
              <ol className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="font-bold text-primary shrink-0">1.</span>
                  <span>You earn or buy <strong className="text-foreground">Sabotage Points (SP)</strong> throughout the season</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-primary shrink-0">2.</span>
                  <span>Spend SP to play a sabotage card against your opponent</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-primary shrink-0">3.</span>
                  <span>Cards apply real-time effects to your opponent's roster</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-primary shrink-0">4.</span>
                  <span>All card plays are <strong className="text-foreground">public</strong>. Everyone sees the chaos</span>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* Example cards */}
          <div className="grid gap-4 sm:grid-cols-3">
            {exampleCards.map(card => (
              <Card key={card.name} className={`border ${card.borderColor} transition-all hover:scale-[1.02]`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${card.accentBg} flex items-center justify-center shrink-0 ${card.color}`}>
                      {card.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm">{card.name}</h4>
                      <Badge className={`${card.rarityBg} ${card.rarityColor} text-[10px] border-0`}>
                        {card.rarity}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{card.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Separator />

        {/* ============================================================ */}
        {/*  SECTION 5: Draft Sabotage Cards                             */}
        {/* ============================================================ */}
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center">
            Draft Sabotage <span className="text-rarity-legendary">Cards</span>
          </h2>
          <p className="text-sm text-muted-foreground text-center max-w-xl mx-auto">
            The chaos starts before the season even begins. Sabotage cards can be played
            DURING the draft to mess with your opponents' picks.
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            {draftCards.map(card => (
              <Card key={card.name} className="border-border transition-all hover:scale-[1.02]">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${card.accentBg} flex items-center justify-center shrink-0 ${card.color}`}>
                      {card.icon}
                    </div>
                    <h4 className="font-bold text-sm">{card.name}</h4>
                  </div>
                  <p className="text-xs text-muted-foreground">{card.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Separator />

        {/* ============================================================ */}
        {/*  SECTION 6: Matchups & Playoffs                              */}
        {/* ============================================================ */}
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center">
            Matchups & <span className="text-accent">Playoffs</span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: <Swords className="w-5 h-5" />, label: 'Weekly H2H', desc: 'Head-to-head matchups every week', color: 'text-primary', bg: 'bg-primary/10' },
              { icon: <Calendar className="w-5 h-5" />, label: '14-Week Season', desc: 'Regular season + playoffs', color: 'text-accent', bg: 'bg-accent/10' },
              { icon: <Users className="w-5 h-5" />, label: 'Top 4 Playoffs', desc: 'Best records advance', color: 'text-purple-400', bg: 'bg-purple-500/10' },
              { icon: <Crown className="w-5 h-5" />, label: 'Championship', desc: 'One winner takes all', color: 'text-amber-400', bg: 'bg-amber-500/10' },
            ].map(item => (
              <Card key={item.label} className="border-border">
                <CardContent className="p-4 text-center space-y-2">
                  <div className={`w-10 h-10 rounded-lg ${item.bg} flex items-center justify-center mx-auto ${item.color}`}>
                    {item.icon}
                  </div>
                  <h4 className="font-bold text-xs">{item.label}</h4>
                  <p className="text-[11px] text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-sm text-muted-foreground text-center">
            Your worst lineup is your best weapon. Every single week.
            Real-time scoring during live NFL games.
          </p>
        </div>

        <Separator />

        {/* ============================================================ */}
        {/*  SECTION 7: Ready to Play?                                   */}
        {/* ============================================================ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-card border border-border p-6 md:p-10 text-center">
          <div className="absolute top-0 left-0 w-56 h-56 bg-damage/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">
              Ready to <span className="text-damage">Sabotage?</span>
            </h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
              Your worst lineup is your best weapon. Time to prove it.
            </p>
            <div className="flex gap-3 flex-wrap justify-center">
              <Button onClick={() => navigate('/auth')} size="lg" className="gap-2">
                <Zap className="w-4 h-4" />
                Get Started
              </Button>
              <Button onClick={() => navigate('/nfldemo')} size="lg" variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 gap-2">
                Try the Demo
              </Button>
            </div>
          </div>
        </div>

    </MarketingLayout>
  );
}
